import {
  ApiReturnProject,
  ApiReturnSite,
  ApiReturnSiteIdsToDashboardIds,
} from '@highimpact-frontend/api-types';
import Stack from '@mui/material/Stack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PermissionDecoratorV2 from '../../utils/permission/PermissionDecoratorV2';

interface ProjectSiteNavigatorComponentProps {
  projectList: ApiReturnProject[];
  siteList: ApiReturnSite[];
  siteIdsToDashboardIds: ApiReturnSiteIdsToDashboardIds;
  section: string;
  getSiteSubset: (projectId: number) => ApiReturnSite[];
  defaultProjectId?: number;
  defaultSiteId?: number;
}

const makeURL = (
  projectId: number,
  siteId: number,
  section: string,
  gridId: number
) => {
  return gridId === -1
    ? `/projects/${projectId}/sites/${siteId}/${section}`
    : `/projects/${projectId}/sites/${siteId}/${section}/${gridId}`;
};

const sortByName = <T extends { name: string }>(items: T[]): T[] => {
  const collator = new Intl.Collator(undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  return [...items].sort((a, b) => collator.compare(a.name, b.name));
};

const ProjectSiteNavigatorComponent: React.FC<
  ProjectSiteNavigatorComponentProps
> = ({
  projectList,
  siteList,
  siteIdsToDashboardIds,
  section,
  getSiteSubset,
  defaultProjectId,
  defaultSiteId,
}) => {
    const { t } = useTranslation('menu');
    const navigate = useNavigate();

    const sortedProjectList = useMemo(() => sortByName(projectList), [projectList]);

    const [selectedProject, setSelectedProject] =
      useState<ApiReturnProject | null>(null);
    const [filteredSiteList, setFilteredSiteList] = useState<ApiReturnSite[]>([]);
    const [selectedSite, setSelectedSite] = useState<number | null>(
      defaultSiteId || null
    );

    const siteIdToGridId = Object.entries(siteIdsToDashboardIds.values).reduce(
      (acc, [siteId, gridIds]) => {
        return gridIds[0]
          ? {
            ...acc,
            [siteId]:
              gridIds.find((dashboard) => dashboard.type === 'user_dashboard')
                ?.id || 0,
          }
          : acc;
      },
      {} as Record<number, number>
    );

    const siteIdToReportId = Object.entries(siteIdsToDashboardIds.values).reduce(
      (acc, [siteId, gridIds]) => {
        return gridIds[0]
          ? {
            ...acc,
            [siteId]:
              gridIds.find((dashboard) => dashboard.type === 'report_dashboard')
                ?.id || 0,
          }
          : acc;
      },
      {} as Record<number, number>
    );

    useEffect(() => {
      const projectId = defaultProjectId;
      if (projectId) {
        const defaultProject =
          sortedProjectList.find((project) => project.id === projectId) || null;
        setSelectedProject(defaultProject);

        if (defaultProject) {
          const subset = sortByName(getSiteSubset(defaultProject.id));
          setFilteredSiteList(subset);
          setSelectedSite(defaultSiteId ?? null);
        }
      }
    }, [
      defaultProjectId,
      defaultSiteId,
      sortedProjectList,
      siteList,
      getSiteSubset,
    ]);

    const handleProjectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = parseInt(event.target.value);
      const selectedProject =
        sortedProjectList.find((project) => project.id === selectedId) || null;
      setSelectedProject(selectedProject);

      if (selectedProject) {
        const subset = sortByName(getSiteSubset(selectedProject.id));
        setFilteredSiteList(subset);
        setSelectedSite(null);
        const firstSite = subset[0];
        const gridId =
          section === 'tableau-de-bord'
            ? siteIdToGridId[firstSite.id] ?? 0
            : section === 'rapports'
              ? siteIdToReportId[firstSite.id] ?? 0
              : -1;
        if (firstSite)
          navigate(makeURL(selectedId, firstSite.id, section, gridId));
      } else {
        setFilteredSiteList([]);
      }
    };

    const handleSiteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = parseInt(event.target.value);
      setSelectedSite(selectedId);
      if (selectedProject) {
        const gridId =
          section === 'tableau-de-bord'
            ? siteIdToGridId[selectedId] ?? 0
            : section === 'rapports'
              ? siteIdToReportId[selectedId] ?? 0
              : -1;
        navigate(makeURL(selectedProject.id, selectedId, section, gridId));
      }
    };

    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <PermissionDecoratorV2 project_id={selectedProject?.id || undefined} site_id={selectedSite || undefined} item={'projects_selector'}>
          <label htmlFor="project" className="uk-form-label">
            {t('Projet')}
          </label>
        </PermissionDecoratorV2>
        <PermissionDecoratorV2
          project_id={selectedProject?.id || undefined} site_id={selectedSite || undefined} item={'projects_selector'}
          style={{ width: '100%' }}
        >
          <select
            id="project"
            onChange={handleProjectChange}
            value={selectedProject?.id || ''}
            className="uk-select"
          >
            <option key={0}>— {t('Sélectionner')} —</option>
            {sortedProjectList.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.name}
              </option>
            ))}
          </select>
        </PermissionDecoratorV2>
        <PermissionDecoratorV2 project_id={selectedProject?.id || undefined} site_id={selectedSite || undefined} item={'sites_selector'}>
          <label htmlFor="site" className="uk-form-label">
            {t('Site')}
          </label>
        </PermissionDecoratorV2>
        <PermissionDecoratorV2 project_id={selectedProject?.id || undefined} site_id={selectedSite || undefined} item={'sites_selector'} style={{ width: '100%' }}>
          <select
            id="site"
            onChange={handleSiteChange}
            value={selectedSite || ''}
            disabled={!selectedProject}
            className="uk-select"
          >
            {filteredSiteList.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.name}
              </option>
            ))}
          </select>
        </PermissionDecoratorV2>
      </Stack>
    );
  };

export default ProjectSiteNavigatorComponent;
